import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { decryptPassword, decodeMetadata } from '../utils/crypto';
import './PasswordShareViewer.css';

interface PasswordShareDetails {
  id: string;
  ciphertext: string;
  iv: string;
  expiresAt: string;
  oneTime: boolean;
  viewCount: number;
  metadata?: string | null;
}

interface PasswordShareViewerProps {
  shareId: string;
}

const formatDateTime = (isoDate: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(isoDate));
  } catch (error) {
    return isoDate;
  }
};

const PasswordShareViewer: React.FC<PasswordShareViewerProps> = ({ shareId }) => {
  const [password, setPassword] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyUsernameSuccess, setCopyUsernameSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [details, setDetails] = useState<PasswordShareDetails | null>(null);

  const keyFromHash = useMemo(() => {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : window.location.hash;
    const params = new URLSearchParams(hash);
    return params.get('k') ?? '';
  }, []);

  useEffect(() => {
    if (!keyFromHash) {
      setError('This link is missing the decryption key. Please open the exact link you received.');
      setIsLoading(false);
    }
  }, [keyFromHash]);

  useEffect(() => {
    if (!keyFromHash) {
      return;
    }

    const controller = new AbortController();

    const loadShare = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`/api/share/${shareId}`, { signal: controller.signal });

        if (response.status === 404) {
          setError('This link is no longer available or has already been viewed.');
          setIsLoading(false);
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to load the shared password.');
        }

        const data: PasswordShareDetails = await response.json();
        const decrypted = await decryptPassword(data.ciphertext, data.iv, keyFromHash);

        setPassword(decrypted);
        setDetails({ ...data });
        setIsLoading(false);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        console.error('Failed to retrieve shared password:', err);
        if (err instanceof DOMException) {
          setError('Failed to decrypt the password. The link may be corrupted or has already been used.');
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to retrieve shared password.');
        }
        setIsLoading(false);
      }
    };

    loadShare();

    return () => {
      controller.abort();
    };
  }, [shareId, keyFromHash]);

  const sharedMetadata = useMemo(() => decodeMetadata(details?.metadata), [details]);

  const sharedLength = useMemo(() => {
    if (!sharedMetadata) {
      return null;
    }
    const lengthValue = (sharedMetadata as Record<string, unknown>)['length'];
    return typeof lengthValue === 'number' ? lengthValue : null;
  }, [sharedMetadata]);

  const sharedUsername = useMemo(() => {
    if (!sharedMetadata) {
      return null;
    }
    const value = (sharedMetadata as Record<string, unknown>)['username'];
    return typeof value === 'string' && value.length > 0 ? value : null;
  }, [sharedMetadata]);

  const handleCopy = useCallback(async () => {
    if (!password) {
      return;
    }
    try {
      await navigator.clipboard.writeText(password);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy shared password:', err);
      setError('Failed to copy password to clipboard.');
    }
  }, [password]);

  const handleCopyUsername = useCallback(async () => {
    if (!sharedUsername) {
      return;
    }
    try {
      await navigator.clipboard.writeText(sharedUsername);
      setCopyUsernameSuccess(true);
      setTimeout(() => setCopyUsernameSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy username:', err);
    }
  }, [sharedUsername]);

  const goHome = useCallback(() => {
    window.location.href = '/';
  }, []);

  return (
    <div className="share-viewer">
      <div className="share-viewer__card">
        <header className="share-viewer__header">
          <h1 className="share-viewer__title">🔗 Secure Password Share</h1>
          <p className="share-viewer__subtitle">This password was encrypted before it reached our servers.</p>
        </header>

        {isLoading && (
          <div className="share-viewer__status">Loading secure password…</div>
        )}

        {!isLoading && error && (
          <div className="share-viewer__error" role="alert">
            {error}
          </div>
        )}

        {!isLoading && !error && (
          <div className="share-viewer__content">
            {sharedUsername && (
              <div className="share-viewer__field">
                <span className="share-viewer__field-label">For</span>
                <div className="share-viewer__password">
                  <input type="text" value={sharedUsername} readOnly className="share-viewer__input" />
                  <button type="button" className="share-viewer__copy" onClick={handleCopyUsername}>
                    {copyUsernameSuccess ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            )}

            <div className="share-viewer__field">
              <span className="share-viewer__field-label">Password</span>
              <div className="share-viewer__password">
                <input type="text" value={password} readOnly className="share-viewer__input" />
                <button type="button" className="share-viewer__copy" onClick={handleCopy}>
                  {copySuccess ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <ul className="share-viewer__meta">
              {details?.expiresAt && (
                <li>
                  <span className="share-viewer__meta-label">Expires:</span>
                  <span>{formatDateTime(details.expiresAt)}</span>
                </li>
              )}
              {typeof sharedLength === 'number' && (
                <li>
                  <span className="share-viewer__meta-label">Password length:</span>
                  <span>{sharedLength}</span>
                </li>
              )}
              <li>
                <span className="share-viewer__meta-label">One-time link:</span>
                <span>{details?.oneTime ? 'Yes' : 'No'}</span>
              </li>
            </ul>
          </div>
        )}

        <footer className="share-viewer__footer">
          <button type="button" className="share-viewer__home-btn" onClick={goHome}>
            Generate your own password
          </button>
        </footer>
      </div>
    </div>
  );
};

export default PasswordShareViewer;
